import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";

import {kmApiLink} from "@pg-mono/km-routes";
import type {HomeMatchConfigurationSourceSection} from "@pg-rp/common-types";

import {getIgnoredNotifyResponse} from "../../../store/utils/get_ignored_notify_response";
import {getFetchHeaders} from "../../km_app/utils/get_fetch_headers";
import {HomeMatchConsentType, MarketingConsentType} from "../constants/user_consents";
import {IUserProfile} from "../interfaces/IUserProfile";
import {UserPhoneVerifyPayload} from "../interfaces/UserPhoneVerifyPayload";

interface IHomeMatchPreferencesPatchPayload extends Partial<Record<HomeMatchConsentType, boolean>> {
    update_source: string;
}

interface IHomeMatchConfigurationPayload {
    is_active: boolean;
    source_section: HomeMatchConfigurationSourceSection;
}

export const userApi = createApi({
    reducerPath: "user",
    baseQuery: fetchBaseQuery({
        baseUrl: "/"
    }),
    tagTypes: ["Preferences", "User", "PreferencesHomeMatch", "ResignationHomeMatch"],
    endpoints: (builder) => ({
        getUserPreferences: builder.query<Record<MarketingConsentType, boolean>, void>({
            query: () => {
                return {
                    url: kmApiLink.user.preferences()
                };
            },
            providesTags: ["Preferences"]
        }),
        getUserProfile: builder.query<IUserProfile, void>({
            query: () => {
                return {
                    url: kmApiLink.user.info()
                };
            },
            providesTags: ["User"]
        }),
        patchUserPreferences: builder.mutation<Record<MarketingConsentType, boolean>, Record<MarketingConsentType, boolean> | {update_source: string}>({
            query: (request) => {
                return {
                    url: kmApiLink.user.preferences(),
                    method: "PATCH",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["Preferences"]
        }),
        getUserHomeMatchPreferences: builder.query<Record<HomeMatchConsentType, boolean>, void>({
            query: () => {
                return {
                    url: kmApiLink.homeMatch.configuration()
                };
            },
            providesTags: ["PreferencesHomeMatch"]
        }),
        patchUserHomeMatchPreferences: builder.mutation<Record<HomeMatchConsentType, boolean>, IHomeMatchPreferencesPatchPayload>({
            query: (request) => {
                return {
                    url: kmApiLink.homeMatch.configuration(),
                    method: "PATCH",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["PreferencesHomeMatch"]
        }),
        putUserHomeMatchPreferences: builder.mutation<Record<HomeMatchConsentType, boolean>, IHomeMatchConfigurationPayload>({
            query: (request) => {
                return {
                    url: kmApiLink.homeMatch.configuration(),
                    method: "PUT",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["PreferencesHomeMatch"]
        }),
        postUserHomeMatchResignationSurvey: builder.mutation<void, {reason_type: number | null; reason_description?: string}>({
            query: (request) => {
                return {
                    url: kmApiLink.homeMatch.resignation(),
                    method: "POST",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["ResignationHomeMatch"]
        }),
        changeUserPassword: builder.mutation<{email: string}, {previous_password: string; new_password: string}>({
            query: (request) => {
                return {
                    url: kmApiLink.user.changePassword(),
                    method: "PUT",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: []
        }),
        updateAccountDetails: builder.mutation<IUserProfile, {name: string; email: string; phone: string}>({
            query: (request) => {
                return {
                    url: kmApiLink.user.info(),
                    method: "PUT",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["User"],
            async onQueryStarted(_arg, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;

                    dispatch(userApi.util.upsertQueryData("getUserProfile", undefined, data));
                } catch {
                    // Mutation failed - leave cache unchanged.
                }
            }
        }),
        patchAccountDetails: builder.mutation<IUserProfile, {name?: string; email?: string; phone?: string}>({
            query: (request) => {
                return {
                    url: kmApiLink.user.info(),
                    method: "PATCH",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["User"],
            async onQueryStarted(_arg, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;

                    dispatch(userApi.util.upsertQueryData("getUserProfile", undefined, data));
                } catch {
                    // Mutation failed - leave cache unchanged.
                }
            }
        }),
        verifyUserData: builder.mutation<void, {verify_email: boolean; verify_phone: boolean}>({
            query: (request) => {
                return {
                    url: kmApiLink.user.resendVerification(),
                    method: "PATCH",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: []
        }),
        postUserHomeMatchPreleadResignation: builder.mutation<void, {login_token: string; resignation_source: string | null}>({
            query: (request) => {
                return {
                    url: kmApiLink.unsubscribe.hm.base({login_token: request.login_token}),
                    method: "POST",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["User"]
        }),
        postUserPhoneVerificationCode: builder.mutation<void, void>({
            query: () => {
                const url = kmApiLink.user.sendPhoneVerificationCode();
                return {
                    url,
                    method: "POST",
                    headers: getFetchHeaders()
                };
            },
            transformErrorResponse: (response) => {
                getIgnoredNotifyResponse(response, [400, 409, 429]);

                if (response.status === 400) {
                    return {data: {error: "Brak numeru tel"}, status: 400};
                }
                if (response.status === 409) {
                    return {data: {error: "Numer już zweryfikowany"}, status: 409};
                }
                if (response.status === 429) {
                    return {data: {error: "Przekroczono limit żądań wygenerowania kodu - spróbuj ponownie później"}, status: 429};
                }
                return response.data;
            },
            invalidatesTags: ["User"]
        }),
        postUserPhoneVerify: builder.mutation<void, UserPhoneVerifyPayload>({
            query: (request) => {
                const url = kmApiLink.user.verifyPhone();
                return {
                    url,
                    method: "POST",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            transformErrorResponse: (response) => {
                getIgnoredNotifyResponse(response, [400, 409]);
                if (response.status === 400) {
                    return {data: {error: "Kod jest nieprawidłowy, spróbuj ponownie"}, status: 400};
                }
                if (response.status === 409) {
                    return {data: {error: "Numer już zweryfikowany"}, status: 409};
                }
                return response.data;
            },
            invalidatesTags: ["User"],
            async onQueryStarted(_arg, {dispatch, getState, queryFulfilled}) {
                try {
                    await queryFulfilled;

                    const cachedProfile = userApi.endpoints.getUserProfile.select()(getState()).data;

                    if (cachedProfile) {
                        dispatch(
                            userApi.util.upsertQueryData("getUserProfile", undefined, {
                                ...cachedProfile,
                                phone_verified: true
                            })
                        );
                    }
                } catch {
                    // Mutation failed - leave cache unchanged.
                }
            }
        })
    })
});

export const selectUserProfile = userApi.endpoints.getUserProfile.select();

export const {
    useChangeUserPasswordMutation,
    useGetUserPreferencesQuery,
    useGetUserProfileQuery,
    useLazyGetUserProfileQuery,
    usePatchUserPreferencesMutation,
    useGetUserHomeMatchPreferencesQuery,
    usePatchUserHomeMatchPreferencesMutation,
    usePutUserHomeMatchPreferencesMutation,
    usePostUserHomeMatchResignationSurveyMutation,
    useUpdateAccountDetailsMutation,
    useVerifyUserDataMutation,
    usePatchAccountDetailsMutation,
    usePostUserHomeMatchPreleadResignationMutation,
    usePostUserPhoneVerificationCodeMutation,
    usePostUserPhoneVerifyMutation
} = userApi;
import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";

import {kmApiLink} from "@pg-mono/km-routes";
import type {HomeMatchConfigurationSourceSection} from "@pg-rp/common-types";

import {getIgnoredNotifyResponse} from "../../../store/utils/get_ignored_notify_response";
import {getFetchHeaders} from "../../km_app/utils/get_fetch_headers";
import {HomeMatchConsentType, MarketingConsentType} from "../constants/user_consents";
import {IUserProfile} from "../interfaces/IUserProfile";
import {UserPhoneVerifyPayload} from "../interfaces/UserPhoneVerifyPayload";

interface IHomeMatchPreferencesPatchPayload extends Partial<Record<HomeMatchConsentType, boolean>> {
    update_source: string;
}

interface IHomeMatchConfigurationPayload {
    is_active: boolean;
    source_section: HomeMatchConfigurationSourceSection;
}

export const userApi = createApi({
    reducerPath: "user",
    baseQuery: fetchBaseQuery({
        baseUrl: "/"
    }),
    tagTypes: ["Preferences", "User", "PreferencesHomeMatch", "ResignationHomeMatch"],
    endpoints: (builder) => ({
        getUserPreferences: builder.query<Record<MarketingConsentType, boolean>, void>({
            query: () => {
                return {
                    url: kmApiLink.user.preferences()
                };
            },
            providesTags: ["Preferences"]
        }),
        getUserProfile: builder.query<IUserProfile, void>({
            query: () => {
                return {
                    url: kmApiLink.user.info()
                };
            },
            providesTags: ["User"]
        }),
        patchUserPreferences: builder.mutation<Record<MarketingConsentType, boolean>, Record<MarketingConsentType, boolean> | {update_source: string}>({
            query: (request) => {
                return {
                    url: kmApiLink.user.preferences(),
                    method: "PATCH",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["Preferences"]
        }),
        getUserHomeMatchPreferences: builder.query<Record<HomeMatchConsentType, boolean>, void>({
            query: () => {
                return {
                    url: kmApiLink.homeMatch.configuration()
                };
            },
            providesTags: ["PreferencesHomeMatch"]
        }),
        patchUserHomeMatchPreferences: builder.mutation<Record<HomeMatchConsentType, boolean>, IHomeMatchPreferencesPatchPayload>({
            query: (request) => {
                return {
                    url: kmApiLink.homeMatch.configuration(),
                    method: "PATCH",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["PreferencesHomeMatch"]
        }),
        putUserHomeMatchPreferences: builder.mutation<Record<HomeMatchConsentType, boolean>, IHomeMatchConfigurationPayload>({
            query: (request) => {
                return {
                    url: kmApiLink.homeMatch.configuration(),
                    method: "PUT",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["PreferencesHomeMatch"]
        }),
        postUserHomeMatchResignationSurvey: builder.mutation<void, {reason_type: number | null; reason_description?: string}>({
            query: (request) => {
                return {
                    url: kmApiLink.homeMatch.resignation(),
                    method: "POST",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["ResignationHomeMatch"]
        }),
        changeUserPassword: builder.mutation<{email: string}, {previous_password: string; new_password: string}>({
            query: (request) => {
                return {
                    url: kmApiLink.user.changePassword(),
                    method: "PUT",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: []
        }),
        updateAccountDetails: builder.mutation<IUserProfile, {name: string; email: string; phone: string}>({
            query: (request) => {
                return {
                    url: kmApiLink.user.info(),
                    method: "PUT",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["User"],
            async onQueryStarted(_arg, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;

                    dispatch(userApi.util.upsertQueryData("getUserProfile", undefined, data));
                } catch {
                    // Mutation failed - leave cache unchanged.
                }
            }
        }),
        patchAccountDetails: builder.mutation<IUserProfile, {name?: string; email?: string; phone?: string}>({
            query: (request) => {
                return {
                    url: kmApiLink.user.info(),
                    method: "PATCH",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["User"],
            async onQueryStarted(_arg, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;

                    dispatch(userApi.util.upsertQueryData("getUserProfile", undefined, data));
                } catch {
                    // Mutation failed - leave cache unchanged.
                }
            }
        }),
        verifyUserData: builder.mutation<void, {verify_email: boolean; verify_phone: boolean}>({
            query: (request) => {
                return {
                    url: kmApiLink.user.resendVerification(),
                    method: "PATCH",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: []
        }),
        postUserHomeMatchPreleadResignation: builder.mutation<void, {login_token: string; resignation_source: string | null}>({
            query: (request) => {
                return {
                    url: kmApiLink.unsubscribe.hm.base({login_token: request.login_token}),
                    method: "POST",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            invalidatesTags: ["User"]
        }),
        postUserPhoneVerificationCode: builder.mutation<void, void>({
            query: () => {
                const url = kmApiLink.user.sendPhoneVerificationCode();
                return {
                    url,
                    method: "POST",
                    headers: getFetchHeaders()
                };
            },
            transformErrorResponse: (response) => {
                getIgnoredNotifyResponse(response, [400, 409, 429]);

                if (response.status === 400) {
                    return {data: {error: "Brak numeru tel"}, status: 400};
                }
                if (response.status === 409) {
                    return {data: {error: "Numer już zweryfikowany"}, status: 409};
                }
                if (response.status === 429) {
                    return {data: {error: "Przekroczono limit żądań wygenerowania kodu - spróbuj ponownie później"}, status: 429};
                }
                return response.data;
            },
            invalidatesTags: ["User"]
        }),
        postUserPhoneVerify: builder.mutation<void, UserPhoneVerifyPayload>({
            query: (request) => {
                const url = kmApiLink.user.verifyPhone();
                return {
                    url,
                    method: "POST",
                    body: request,
                    headers: getFetchHeaders()
                };
            },
            transformErrorResponse: (response) => {
                getIgnoredNotifyResponse(response, [400, 409]);
                if (response.status === 400) {
                    return {data: {error: "Kod jest nieprawidłowy, spróbuj ponownie"}, status: 400};
                }
                if (response.status === 409) {
                    return {data: {error: "Numer już zweryfikowany"}, status: 409};
                }
                return response.data;
            },
            invalidatesTags: ["User"],
            async onQueryStarted(_arg, {dispatch, getState, queryFulfilled}) {
                try {
                    await queryFulfilled;

                    const cachedProfile = userApi.endpoints.getUserProfile.select()(getState()).data;

                    if (cachedProfile) {
                        dispatch(
                            userApi.util.upsertQueryData("getUserProfile", undefined, {
                                ...cachedProfile,
                                phone_verified: true
                            })
                        );
                    }
                } catch {
                    // Mutation failed - leave cache unchanged.
                }
            }
        })
    })
});

export const selectUserProfile = userApi.endpoints.getUserProfile.select();

export const {
    useChangeUserPasswordMutation,
    useGetUserPreferencesQuery,
    useGetUserProfileQuery,
    useLazyGetUserProfileQuery,
    usePatchUserPreferencesMutation,
    useGetUserHomeMatchPreferencesQuery,
    usePatchUserHomeMatchPreferencesMutation,
    usePutUserHomeMatchPreferencesMutation,
    usePostUserHomeMatchResignationSurveyMutation,
    useUpdateAccountDetailsMutation,
    useVerifyUserDataMutation,
    usePatchAccountDetailsMutation,
    usePostUserHomeMatchPreleadResignationMutation,
    usePostUserPhoneVerificationCodeMutation,
    usePostUserPhoneVerifyMutation
} = userApi;
